import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { TabType } from '../../../components/tabs';
import { useInstallationContext } from '../../../context/installation';
import { IntegrationType, UserPermissionType } from '../../../data-access/gql-types/graphql';
import { environment } from '../../../environment';
import { useNative } from '../../../hooks/native/use-native';
import { useUserPermissions } from '../../../hooks/user/use-user-permissions';
import { useLoggedUser } from '../../../integrations/exalus/hooks/common/logged-user';
import ExalusControllerSettings from '../../../integrations/exalus/modules/controller-settings';
import { isLocalApp } from '../../../utils/helpers/local-app';
import { pathnameWithParameters } from '../../../utils/location';
import InstallationSettings from '../../installation-settings';
import ApplicationSettings from '../application';
import Device from '../device';
import Feedback from '../feedback';
import Notifications from '../notifications';
import Policy from '../policy';
import ProfileSettings from '../profile';
import Terms from '../terms';

export const useSettings = () => {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const { t } = useTranslation('profile');
  const { t: ti } = useTranslation('installation');
  const [notificationTutorial, setNotificationTutorial] = useState<boolean>(false);
  const { integrationType, selectedInstallationId } = useInstallationContext();
  const { canDeviceEdit } = useLoggedUser();
  const { checkForVibrationSupported } = useNative();
  const [activeTabIndex, setActiveTabIndex] = useState<number>(0);
  const { permissions } = useUserPermissions();

  useEffect(() => {
    const tab = new URLSearchParams(search).get('tab');
    if (tab) setActiveTabIndex(parseInt(tab));
  }, []);

  const deviceListVisible = useMemo(() => {
    if (integrationType === IntegrationType.Exalus) return canDeviceEdit;

    return permissions[UserPermissionType.DeviceList];
  }, [integrationType, permissions, canDeviceEdit]);

  const isExalus = useMemo(() => integrationType === IntegrationType.Exalus, [integrationType]);

  const tabsAndPages = useMemo(
    () => [
      {
        tabLabel: ti('exalus.settingsController'),
        component: <ExalusControllerSettings key={1} noHeader />,
        enabled: isExalus,
      },
      {
        tabLabel: ti('settings'),
        component: <InstallationSettings id={selectedInstallationId} />,
        enabled: !!selectedInstallationId && !isLocalApp,
      },
      {
        tabLabel: t('devicesList'),
        component: <Device />,
        enabled: deviceListVisible && !isLocalApp,
      },
      {
        tabLabel: t('yourApp'),
        component: <ApplicationSettings />,
        enabled: true,
      },
      {
        tabLabel: t('notifications'),
        component: <Notifications tutorial={notificationTutorial} openTutorial={setNotificationTutorial} />,
        enabled: !isLocalApp,
      },
      {
        tabLabel: t('yourProfile'),
        component: <ProfileSettings />,
        enabled: true,
      },
      {
        tabLabel: t('support'),
        component: <Feedback />,
        enabled: true,
      },
      {
        tabLabel: t('policy'),
        component: <Policy />,
        enabled: true,
      },
      {
        tabLabel: t('terms'),
        component: <Terms />,
        enabled: true,
      },
    ],
    [
      selectedInstallationId,
      deviceListVisible,
      isExalus,
      isLocalApp,
      environment.INTEGRATION_DEFAULT,
      notificationTutorial,
      t,
      ti,
    ],
  );

  const handleTabChange = (tabIndex: number) => {
    const appSettingsIndex = tabsAndPages.filter((x) => x.enabled).findIndex((x) => x.tabLabel === t('yourApp'));
    if (appSettingsIndex === tabIndex) checkForVibrationSupported();

    setActiveTabIndex(tabIndex);
    navigate(pathnameWithParameters(pathname, search, { key: 'tab', value: tabIndex }), { replace: true });
  };

  const tabs: TabType[] = useMemo(
    () => tabsAndPages.filter((x) => x.enabled).map((x) => ({ label: x.tabLabel })),
    [tabsAndPages],
  );

  const components: React.ReactNode[] = useMemo(
    () => tabsAndPages.filter((x) => x.enabled).map((x) => x.component),
    [tabsAndPages],
  );

  return {
    tabs,
    activeTabIndex,
    handleTabChange,
    components,
    tabsAndPages,
    notificationTutorial,
  };
};
